import { CommonModule } from '@angular/common';
import { Component, input, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { LocalePathPipe } from 'src/app/pipes/locale-path.pipe';
import { LoadingController, ToastController } from '@ionic/angular/standalone';

import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonInput,
  IonInputPasswordToggle,
  IonText,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { InvalidCredentialsError } from 'src/app/services/auth/auth.model';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { PushNotificationsService } from 'src/app/services/push-notifications/push-notifications.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { EventBusService } from 'src/app/services/eventbus/eventbus.service';

@Component({
  selector: 'app-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  standalone: true,
  imports: [
    IonText,
    IonCardTitle,
    IonCardHeader,
    IonCard,
    TranslateModule,
    IonCardContent,
    IonButton,
    IonInput,
    RouterLink,
    LocalePathPipe,
    CommonModule,
    ReactiveFormsModule,
    IonInputPasswordToggle,
  ],
})
export class LoginFormComponent {
  private readonly logger = LoggerService.create(LoginFormComponent.name);

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private loadingController: LoadingController,
    private toastController: ToastController,
    private translateService: TranslateService,
    private toastService: ToastService,
    private pushNotificationsService: PushNotificationsService,
    private eventBusService: EventBusService
  ) {}

  hideHeader = input<boolean>(false);

  loginSuccessEvent = output<void>();
  signupClickedEvent = output<void>();
  forgotPasswordClickedEvent = output<void>();

  isSubmitting = signal(false);

  form = this.fb.group({
    email: ['', [Validators.required]],
    password: ['', [Validators.required]],
  });

  validationMessage = signal<string>('');
  toast: HTMLIonToastElement | null = null;

  async onSubmit() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.toast?.dismiss();

    this.validationMessage.set('');

    if (!this.form.valid) {
      this.validationMessage.set('crowd_login_required_fields');

      return;
    }

    const { email, password } = this.form.value;
    const loading = await this.displayLoading();

    try {
      const user = await this.authService.authenticate(email!, password!);

      this.logger.info({ identifier: email!, message: 'User authenticated successfully', method: 'onSubmit' });

      await loading.dismiss();
      await this.displaySuccessToast(user.firstname);

      this.form.reset();
      this.loginSuccessEvent.emit();
      this.eventBusService.publish({ name: 'loginSuccess' });
    } catch (error: any) {
      await loading.dismiss();

      if (error instanceof InvalidCredentialsError) {
        this.validationMessage.set('crowd_login_invalid_credentials');

        return;
      }

      this.logger.error({
        identifier: email ?? '',
        message: 'Login failed with unexpected error',
        method: 'onSubmit',
        error,
      });

      this.toast = await this.toastService.errorToast(
        this.translateService.instant('crowd_login_default_error'),
        error
      );

      captureException(error, { tags: { context: LoginFormComponent.name } });

      await this.toast?.present();

      return;
    }

    await this.pushNotificationsService.registerNotifications();
  }

  private async displayLoading() {
    const message = this.translateService.instant('crowd_login_auth_please_wait');

    const loading = await this.loadingController.create({
      message,
    });

    await loading.present();

    return loading;
  }

  private displaySuccessToast(firstname?: string) {
    let message = this.translateService.instant('crowd_login_welcome');

    if (firstname) {
      message = this.translateService.instant('crowd_login_welcome_back', {
        name: firstname,
      });
    }

    this.toastController
      .create({
        color: 'success',
        message: message,
        position: 'top',
        cssClass: 'text-center',
        duration: 2000,
      })
      .then((toast) => {
        toast.present();
      });
  }

  onForgotPasswordClicked() {
    this.forgotPasswordClickedEvent.emit();
  }
}
